import clsx from 'clsx'
import { useState } from 'react'

const DEFAULT_AVATAR_URL = 'https://storage.googleapis.com/cartwheel-assets/default-user-avatar.png'

export interface AvatarProps {
  src: string
  alt: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  /**
   * Escape hatch to fully control the image's width/height classes (e.g. for
   * responsive sizing like `w-14 h-14 sm:w-16 sm:h-16`). When provided it
   * replaces the fixed `size` classes, so callers should supply both
   * dimensions. Defaults to the `size` token.
   */
  sizeClassName?: string
  onClick?: () => void
  className?: string
  fallbackSrc?: string
}

export function Avatar({
  src,
  alt,
  size = 'md',
  sizeClassName,
  onClick,
  className,
  fallbackSrc = DEFAULT_AVATAR_URL,
}: AvatarProps) {
  const [imgSrc, setImgSrc] = useState(src || fallbackSrc)
  const [hasError, setHasError] = useState(false)
  const [prevSrc, setPrevSrc] = useState(src)

  // Re-sync the displayed image when the `src` prop changes on a reused instance
  // (e.g. picking a different person in a dropdown keeps the same Avatar node).
  // Without this, the mount-only `useState` initializer would keep the first src.
  if (src !== prevSrc) {
    setPrevSrc(src)
    setImgSrc(src || fallbackSrc)
    setHasError(false)
  }

  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
    xl: 'w-14 h-14',
    '2xl': 'w-16 h-16',
  }

  const handleImageError = () => {
    if (!hasError && fallbackSrc && imgSrc !== fallbackSrc) {
      setImgSrc(fallbackSrc)
      setHasError(true)
    }
  }

  const Component = onClick ? 'button' : 'div'

  return (
    <Component
      onClick={onClick}
      className={clsx(
        'flex text-sm rounded-full',
        onClick && 'focus:ring-4 focus:ring-gray-300 cursor-pointer',
        className
      )}
    >
      <span className="sr-only">{onClick ? 'Open user menu' : alt}</span>
      <img
        className={clsx('rounded-full', sizeClassName ?? sizeClasses[size])}
        src={imgSrc}
        alt={alt}
        onError={handleImageError}
        data-dd-privacy="allow"
      />
    </Component>
  )
}
